import React, {
    useEffect,
    useState
} from "react";
import {
    ShieldCheck, Zap, Wallet, CreditCard, Coins, Pickaxe, BarChart3, TrendingUp, Globe2, Clock, Lock, Layers, Users, Boxes,
    Network, Infinity as InfinityIcon, Star,
    HelpCircle
} from "lucide-react";
import {
    useParams
} from "react-router-dom";

import {
    Helmet
} from "react-helmet-async";

import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    CircularProgress
} from "@mui/material";

import ExpandMoreIcon from
    "@mui/icons-material/ExpandMore";
import "../../assets/style.css";
import {
    getDynamicPage,
    getMarketData
} from "../../core/services/seoPageService";
import Error404Content from "../../Pages/Error404Content";

const leftBenefits = [<Network />, <InfinityIcon />, <ShieldCheck />, <Globe2 />, <Lock />, <Coins />];

const rightBenefits = [<Zap />, <Clock />, <TrendingUp />, <BarChart3 />, <Users />, <Boxes />];

const ecosystem = [<Zap />, <Wallet />, <CreditCard />, <Layers />, <Pickaxe />, <BarChart3 />];

const RANGES = [
    "1D",
    "7D",
    "1M",
    "3M",
    "1Y",
    "YTD",
    "ALL"
];


const formatMoney = (
    value, currency
) => {

    if (
        value === null ||
        value === undefined
    ) {
        return "-";
    }

    if (value >= 1e12) {
        return `$${(
            value / 1e12
        ).toFixed(2)}T`;
    }

    if (value >= 1e9) {
        return `$${(
            value / 1e9
        ).toFixed(2)}B`;
    }

    if (value >= 1e6) {
        return `$${(
            value / 1e6
        ).toFixed(2)}M`;
    }

    return new Intl.NumberFormat(
        "en-US",
        {
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 8
        }
    ).format(value);
};


const formatSupply = (
    value,
    symbol
) => {

    if (!value) {
        return "-";
    }

    if (value >= 1e9) {
        return `${(
            value / 1e9
        ).toFixed(2)}B ${symbol}`;
    }

    if (value >= 1e6) {
        return `${(
            value / 1e6
        ).toFixed(2)}M ${symbol}`;
    }

    return `${Number(
        value
    ).toLocaleString()} ${symbol}`;
};


const SeoPage = ({
    forWhat = "exchange"
}) => {

    const { dynCMS: slug, dynCMS2: slug2 } =
        useParams();

    const [
        page,
        setPage
    ] = useState(null);

    const [
        marketData,
        setMarketData
    ] = useState(null);

    const [
        range,
        setRange
    ] = useState("1Y");

    const [
        loading,
        setLoading
    ] = useState(true);


    useEffect(() => {

        loadPage();

    }, [slug, slug2, forWhat]);


    useEffect(() => {

        if (
            page?.market?.enabled &&
            page?.market?.symbol
        ) {
            loadMarketData();
        }

    }, [page, range]);


    const loadPage =
        async () => {

            try {

                setLoading(true);

                const response =
                    await getDynamicPage(
                        forWhat,
                        slug,
                        slug2
                    );

                setPage(
                    response.data.data
                );

            } catch (error) {

                console.error(
                    error
                );

            } finally {

                setLoading(false);

            }
        };


    const loadMarketData =
        async () => {

            try {

                const response =
                    await getMarketData({
                        symbol:
                            page.market.symbol,

                        cmcId:
                            page.market.cmcId,
                        currency:
                            page.market.currency,

                        range
                    });

                setMarketData(
                    response.data.data
                );

            } catch (error) {

                console.error(
                    "Market error",
                    error
                );

            }
        };


    if (loading) {
        return (
            <div className="dynamic-page-loader">

                <CircularProgress />

            </div>
        );
    }


    if (!page) {
        return (
            <div>

                <Error404Content />

            </div>
        );
    }


    const stats =
        marketData?.stats;


    const faqJsonLd = {
        "@context":
            "https://schema.org",

        "@type":
            "FAQPage",

        mainEntity:
            page.faq?.items?.map(
                item => ({
                    "@type":
                        "Question",

                    name:
                        item.question,

                    acceptedAnswer: {
                        "@type":
                            "Answer",

                        text:
                            item.answer
                    }
                })
            ) || []
    };


    return (

        <div className="dynamic-seo-page">

            <Helmet>

                <title>
                    {
                        page.seo?.metaTitle ||
                        page.seo?.pageTitle
                    }
                </title>


                <meta
                    name="description"
                    content={
                        page.seo?.metaDescription
                    }
                />


                {page.seo?.canonicalUrl && (

                    <link
                        rel="canonical"
                        href={
                            page.seo.canonicalUrl
                        }
                    />

                )}


                <meta
                    property="og:title"
                    content={
                        page.seo?.ogTitle ||
                        page.seo?.metaTitle
                    }
                />


                <meta
                    property="og:description"
                    content={
                        page.seo?.ogDescription ||
                        page.seo?.metaDescription
                    }
                />


                {page.seo?.ogImage?.url && (

                    <meta
                        property="og:image"
                        content={
                            page.seo.ogImage.url
                        }
                    />

                )}


                <meta
                    name="twitter:card"
                    content={
                        page.seo?.twitterCard ||
                        "summary_large_image"
                    }
                />


                {page.faq?.items?.length > 0 && (

                    <script
                        type="application/ld+json"
                    >
                        {
                            JSON.stringify(
                                faqJsonLd
                            )
                        }
                    </script>

                )}

            </Helmet>


            {/* =====================================
          HERO
      ===================================== */}

            {page.hero?.enabled && (

                <section className="seo-section hero-section">

                    <div className="hero-grid">

                        <div className="hero-content">

                            {page.hero.badge && (

                                <div className="seo-badge mb-2">

                                    {
                                        page.hero.badge
                                    }

                                </div>

                            )}


                            <h1>

                                {page.hero.title}{" "}

                                <span>

                                    {
                                        page.hero.highlightedTitle
                                    }

                                </span>

                                {" "}

                                {
                                    page.hero.titleSuffix
                                }

                            </h1>


                            <p>

                                {
                                    page.hero.description
                                }

                            </p>


                            <div className="hero-actions">

                                {page.hero.button?.text && (

                                    <a
                                        className="primary-button"
                                        href={
                                            page.hero.button.url
                                        }
                                        target={
                                            page.hero.button.external
                                                ? "_blank"
                                                : "_self"
                                        }
                                        rel="noreferrer"
                                    >

                                        {
                                            page.hero.button.text
                                        }

                                        <span>
                                            ↗
                                        </span>

                                    </a>

                                )}



                            </div>
                            {/* {page.hero.trustCards?.map(
                                    (
                                        card,
                                        index
                                    ) => (

                                        <div
                                            className="trust-card"
                                            key={index}
                                        >

                                            <strong>

                                                {
                                                    card.title ||
                                                    card.value
                                                }

                                            </strong>

                                            <small>

                                                {
                                                    card.subtitle
                                                }

                                            </small>

                                        </div>

                                    )
                                )} */}
                            <div className="hero-trust-wrapper mt-2">

                                {/* FIU Card */}
                                <div className="hero-trust-card">

                                    <div className="hero-trust-icon">
                                        <ShieldCheck size={20} />
                                    </div>

                                    <div className="hero-trust-content">

                                        <div className="hero-trust-title">
                                            FIU
                                        </div>

                                        <div className="hero-trust-subtitle">
                                            Compliant
                                        </div>

                                    </div>

                                </div>


                                {/* Google Rating Card */}
                                <div className="hero-trust-card">

                                    <div className="google-rating-icon">
                                        G
                                    </div>

                                    <div className="hero-trust-content">

                                        <div className="hero-trust-subtitle">
                                            Google Rating
                                        </div>

                                        <div className="google-rating-row">

                                            <span className="google-rating-number">
                                                4.8
                                            </span>

                                            <div className="google-stars">

                                                {Array.from(
                                                    { length: 5 }
                                                ).map((_, i) => (

                                                    <Star
                                                        key={i}
                                                        size={13}
                                                        fill="currentColor"
                                                    />

                                                ))}

                                            </div>

                                        </div>

                                    </div>

                                </div>

                            </div>

                        </div>


                        {page.hero.image?.url && (

                            <div className="hero-image-wrap">

                                <img
                                    src={
                                        page.hero.image.url
                                    }
                                    alt={
                                        page.hero.image.alt ||
                                        page.hero.title
                                    }
                                />

                            </div>

                        )}

                    </div>

                </section>

            )}


            {/* =====================================
          ABOUT
      ===================================== */}

            {page.about?.enabled && (

                <section className="seo-section">

                    <div className="about-grid">

                        {page.about.image?.url && (

                            <img
                                className="about-image"
                                src={
                                    page.about.image.url
                                }
                                alt={
                                    page.about.image.alt ||
                                    page.about.title
                                }
                            />

                        )}


                        <div>

                            <h2>

                                {
                                    page.about.title
                                }{" "}

                                <span>

                                    {
                                        page.about.highlightedTitle
                                    }

                                </span>

                            </h2>


                            {page.about.paragraphs?.map(
                                (
                                    paragraph,
                                    index
                                ) => (

                                    <p
                                        key={index}
                                    >

                                        {
                                            paragraph
                                        }

                                    </p>

                                )
                            )}

                        </div>

                    </div>

                </section>

            )}


            {/* =====================================
          PRICE PERFORMANCE
      ===================================== */}

            {page.pricePerformance?.enabled && (

                <section className="seo-section">

                    <div className="performance-grid">

                        <div>

                            <h2>

                                {
                                    page.pricePerformance
                                        .title
                                }{" "}

                                <span>

                                    {
                                        page.pricePerformance
                                            .highlightedTitle
                                    }

                                </span>

                            </h2>


                            <div className="range-buttons">

                                {RANGES.map(item => (

                                    <button
                                        key={item}
                                        className={
                                            range === item
                                                ? "active"
                                                : ""
                                        }
                                        onClick={() =>
                                            setRange(item)
                                        }
                                    >

                                        {item}

                                    </button>

                                ))}

                            </div>


                            <SimpleChart
                                data={
                                    marketData?.chart ||
                                    []
                                }
                            />

                        </div>


                        {page.pricePerformance
                            ?.statsEnabled && (

                                <div className="market-stats-card">

                                    <StatRow
                                        label="Current Price"
                                        value={
                                            formatMoney(
                                                stats?.currentPrice,
                                                page.market.currency
                                            )
                                        }
                                        accent
                                    />


                                    <StatRow
                                        label="24h Change"
                                        value={
                                            stats
                                                ? `${stats.change24h >= 0 ? "▲" : "▼"} ${Math.abs(stats.change24h).toFixed(2)}%`
                                                : "-"
                                        }
                                        accent={
                                            stats?.change24h >= 0
                                        }
                                        negative={
                                            stats?.change24h < 0
                                        }
                                    />


                                    <StatRow
                                        label="Market Cap"
                                        value={
                                            formatMoney(
                                                stats?.marketCap, page.market.currency
                                            )
                                        }
                                    />


                                    <StatRow
                                        label="24h Volume"
                                        value={
                                            formatMoney(
                                                stats?.volume24h, page.market.currency
                                            )
                                        }
                                    />


                                    <StatRow
                                        label="Circulating Supply"
                                        value={
                                            formatSupply(
                                                stats?.circulatingSupply,
                                                page.market.symbol
                                            )
                                        }
                                    />


                                    <StatRow
                                        label="Max Supply"
                                        value={
                                            formatSupply(
                                                stats?.maxSupply,
                                                page.market.symbol
                                            )
                                        }
                                    />


                                    {page.pricePerformance
                                        ?.button?.text && (

                                            <a
                                                className="chart-button"
                                                href={
                                                    page.pricePerformance
                                                        .button.url ||
                                                    page.market
                                                        .tradingViewUrl
                                                }
                                                target="_blank"
                                                rel="noreferrer"
                                            >

                                                ↗ {" "}

                                                {
                                                    page.pricePerformance
                                                        .button.text
                                                }

                                            </a>

                                        )}

                                </div>

                            )}

                    </div>

                </section>

            )}


            {/* =====================================
          BENEFITS
      ===================================== */}

            {page.benefits?.enabled && (

                <section className="seo-section">

                    <h2 className="center-title">

                        {
                            page.benefits.title
                        }{" "}

                        <span>

                            {
                                page.benefits
                                    .highlightedTitle
                            }

                        </span>

                    </h2>


                    <div className="benefits-grid">

                        <div className="benefit-column left">

                            {page.benefits.leftItems?.map(
                                (
                                    item,
                                    index
                                ) => {
                                    const setIcon = leftBenefits?.[index]
                                    return (

                                        <BenefitItem
                                            key={index}
                                            item={item}
                                            icon={setIcon}
                                        />

                                    )
                                }
                            )}

                        </div>


                        <div className="benefit-center">

                            {page.benefits
                                .centerImage?.url && (

                                    <img
                                        src={
                                            page.benefits
                                                .centerImage.url
                                        }
                                        alt=""
                                    />

                                )}

                        </div>


                        <div className="benefit-column">

                            {page.benefits.rightItems?.map(
                                (
                                    item,
                                    index
                                ) => {
                                    const setIcon = rightBenefits?.[index]
                                    return (

                                        <BenefitItem
                                            key={index}
                                            item={item}
                                            icon={setIcon}
                                        />

                                    )
                                }
                            )}

                        </div>

                    </div>

                </section>

            )}


            {/* =====================================
          ECOSYSTEM
      ===================================== */}

            {page.ecosystem?.enabled && (

                <section className="seo-section">

                    <h2 className="center-title">

                        {
                            page.ecosystem.title
                        }{" "}

                        <span>

                            {
                                page.ecosystem
                                    .highlightedTitle
                            }

                        </span>

                    </h2>


                    <div className="ecosystem-grid">

                        {page.ecosystem.items?.map(
                            (
                                item,
                                index
                            ) => {
                                const setIcon = ecosystem?.[index]
                                return (

                                    <div
                                        className="ecosystem-card"
                                        key={index}
                                    >

                                        <div className="card-icon">

                                            {
                                                setIcon ||
                                                "✦"
                                            }

                                        </div>

                                        <h3>

                                            {
                                                item.title
                                            }

                                        </h3>

                                        <p>

                                            {
                                                item.description
                                            }

                                        </p>

                                    </div>

                                )
                            }
                        )}

                    </div>

                </section>

            )}


            {/* =====================================
          DATA SOURCES
      ===================================== */}

            {page.dataSources?.enabled && (

                <section className="seo-section">
                    <h2 className="center-title">

                        {
                            page.dataSources.title
                        }{" "}

                        <span>

                            {
                                page.dataSources
                                    .highlightedTitle
                            }

                        </span>

                    </h2>


                    <div className="source-grid">

                        {page.dataSources.items?.map(
                            (
                                item,
                                index
                            ) => (

                                <div
                                    className="source-card"
                                    key={index}
                                >
                                    <div className="d-flex justify-content-start align-items-center" style={{ gap: '10px' }}>
                                        <div className="data-icon">

                                            <BarChart3 />

                                        </div>
                                        <h3>

                                            {
                                                item.name
                                            }

                                        </h3>
                                    </div>

                                    <p>

                                        {
                                            item.description
                                        }

                                    </p>

                                    <a
                                        href={item.url}
                                        target="_blank"
                                        rel="noreferrer"
                                    >

                                        {
                                            item.buttonText
                                        }

                                        {" "}↗

                                    </a>

                                </div>

                            )
                        )}

                    </div>

                </section>

            )}


            {/* =====================================
          FAQ
      ===================================== */}

            {page.faq?.enabled && (

                <section className="seo-section">

                    <h2 className="center-title">

                        {
                            page.faq.title
                        }{" "}

                        <span>

                            {
                                page.faq.highlightedTitle
                            }

                        </span>

                    </h2>


                    <div className="faq-wrapper">

                        {page.faq.items?.map(
                            (
                                item,
                                index
                            ) => (

                                <Accordion
                                    key={index}
                                    className="faq-item"
                                >

                                    <AccordionSummary
                                        expandIcon={
                                            <ExpandMoreIcon />
                                        }
                                    >

                                        <strong>
                                            <HelpCircle style={{ color: '#42bdb5', width: '18px', height: '18px' }} />{" "}{" "}{" "}
                                            {index + 1}.
                                            {" "}
                                            {
                                                item.question
                                            }

                                        </strong>

                                    </AccordionSummary>

                                    <AccordionDetails>

                                        {
                                            item.answer
                                        }

                                    </AccordionDetails>

                                </Accordion>

                            )
                        )}

                    </div>


                    {page.faq.bottomBadge && (

                        <div className="bottom-badge">

                            {
                                page.faq.bottomBadge
                            }

                        </div>

                    )}

                </section>

            )}

        </div>

    );
};


const StatRow = ({
    label,
    value,
    accent = false,
    negative = false
}) => (

    <div className="stat-row">

        <span>
            {label}
        </span>

        <strong
            className={
                negative
                    ? "negative"
                    : accent
                        ? "positive"
                        : ""
            }
        >

            {value}

        </strong>

    </div>

);

const BenefitItem = ({
    item, icon
}) => (

    <div className="benefit-item">

        <div className="benefit-icon">

            {
                icon || "✦"
            }

        </div>

        <div>

            <h3>

                {
                    item.title
                }

            </h3>

            <p>

                {
                    item.description
                }

            </p>

        </div>

    </div>

);


/*
|--------------------------------------------------------------------------
| SIMPLE SVG CHART
|--------------------------------------------------------------------------
*/

const SimpleChart = ({ data }) => {

    if (!data?.length) {

        return (

            <div className="chart-empty">
                Loading chart...
            </div>

        );

    }


    const values =
        data.map(
            item => Number(item.value) || 0
        );


    const max =
        Math.max(...values);


    const min =
        Math.min(...values);


    const width = 660;
    const height = 220;

    const paddingTop = 14;
    const paddingBottom = 10;


    const normalizedHeight =
        height -
        paddingTop -
        paddingBottom;


    const pointsArray =
        values.map(
            (value, index) => {

                const x =
                    (index /
                        Math.max(
                            values.length - 1,
                            1
                        )) *
                    width;


                const y =
                    paddingTop +
                    (
                        1 -
                        (
                            (value - min) /
                            Math.max(
                                max - min,
                                1
                            )
                        )
                    ) *
                    normalizedHeight;


                return {
                    x,
                    y,
                    value,
                    date: data[index]?.date ||
                        data[index]?.time ||
                        ""
                };

            }
        );


    const points =
        pointsArray
            .map(
                point =>
                    `${point.x},${point.y}`
            )
            .join(" ");


    const linePath =
        pointsArray.reduce(
            (
                path,
                point,
                index
            ) => {

                return (
                    index === 0
                        ? `M ${point.x} ${point.y}`
                        : `${path} L ${point.x} ${point.y}`
                );

            },
            ""
        );


    const lastPoint =
        pointsArray[
        pointsArray.length - 1
        ];


    const areaPath =
        `${linePath}
        L ${lastPoint.x} ${height}
        L 0 ${height}
        Z`;


    const getLabel =
        index => {

            if (!data[index]) {
                return "";
            }

            const value =
                data[index]?.date ||
                data[index]?.time;

            if (!value) {
                return "";
            }

            try {

                return new Date(value)
                    .toLocaleDateString(
                        "en-US",
                        {
                            month: "short",
                            year: "numeric"
                        }
                    );

            } catch {

                return value;

            }

        };


    const labelIndexes = [
        0,
        Math.floor(data.length * .16),
        Math.floor(data.length * .33),
        Math.floor(data.length * .5),
        Math.floor(data.length * .66),
        Math.floor(data.length * .83),
        data.length - 1
    ];


    return (

        <div className="chart-container">

            <div className="chart-tooltip">

                <strong>
                    {formatMoney(lastPoint?.value)}
                </strong>

                <small>
                    {
                        lastPoint?.date
                            ? new Date(
                                lastPoint.date
                            ).toLocaleDateString(
                                "en-US",
                                {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric"
                                }
                            )
                            : ""
                    }
                </small>

            </div>


            <svg
                viewBox={`0 0 ${width} ${height}`}
                preserveAspectRatio="none"
            >

                <defs>

                    <linearGradient
                        id="seoChartFill"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                    >

                        <stop
                            offset="0%"
                            stopColor="#42bdb5"
                            stopOpacity="0.32"
                        />

                        <stop
                            offset="100%"
                            stopColor="#42bdb5"
                            stopOpacity="0"
                        />

                    </linearGradient>

                </defs>


                {[0, 1, 2, 3, 4, 5].map(
                    item => {

                        const y =
                            10 +
                            item * 38;

                        return (

                            <line
                                key={item}
                                x1="0"
                                x2={width}
                                y1={y}
                                y2={y}
                                className="chart-grid-line"
                            />

                        );

                    }
                )}


                <path
                    d={areaPath}
                    fill="url(#seoChartFill)"
                />


                <path
                    d={linePath}
                    fill="none"
                    stroke="#42bdb5"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />


                {lastPoint && (

                    <circle
                        cx={lastPoint.x}
                        cy={lastPoint.y}
                        r="4"
                        className="chart-last-point"
                    />

                )}

            </svg>


            <div className="chart-labels">

                {labelIndexes.map(
                    (
                        index,
                        labelIndex
                    ) => (

                        <span
                            key={`${index}-${labelIndex}`}
                        >

                            {
                                getLabel(index)
                            }

                        </span>

                    )
                )}

            </div>

        </div>

    );

};


export default SeoPage;